import { useEffect, useImperativeHandle, useState } from 'react';

import { ajvResolver } from '@hookform/resolvers/ajv';
import { Stack } from '@mui/material';
import { JSONSchemaType } from 'ajv';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { OrganisationsAutocomplete } from '~components';
import ResourceGroupAutocomplete from '~components/inputFields/autocompletes/resourceGroupAutocomplete';
import AssignKeyFormResults from '~features/keys/interfaces/assignKeyFormResults';
import { useFormContainer } from '~hooks';
import ResourceGroup from '~interfaces/resourceGroup';

import schema from './assignKeySchema.json';
import Dimens from '../../../../themes/dimens';

interface RemoteActionsDialogProps {
	showEditOrganisation?: boolean;
	currentlyAssignedOrganisationId?: string | undefined;
	onSubmit: (results: AssignKeyFormResults) => void;
	onCancel: () => void;
}

interface AssignKeyFormFields {
	organisationId: string;
	resourceGroupId: string;
}

const AssignKeyForm = ({
	showEditOrganisation = true,
	currentlyAssignedOrganisationId,
	onSubmit,
	onCancel,
}: RemoteActionsDialogProps) => {
	const { t } = useTranslation();
	const { setDisabled, formRef } = useFormContainer();
	const [resourceGroupDisabled, setResourceGroupDisabled] = useState(true);
	const [selectedResourceGroup, setSelectedResourceGroup] = useState<ResourceGroup | undefined>();
	const [selectedOrganisationId, setSelectedOrganisationId] = useState<string | undefined>(
		currentlyAssignedOrganisationId
	);

	const { getValues, control, formState, watch } = useForm<AssignKeyFormResults>({
		mode: 'onChange',
		resolver: ajvResolver(schema as JSONSchemaType<AssignKeyFormFields>),
	});

	useImperativeHandle(
		formRef,
		() => ({
			onSubmit: () => onSubmit?.(getValues() as AssignKeyFormResults),
			onCancel: () => onCancel?.(),
		}),
		[onSubmit, onCancel, getValues]
	);

	const organisationValue = watch('organisationId');
	const resourceGroupValue = watch('resourceGroupId');

	const organisationFieldValid =
		(showEditOrganisation && organisationValue != undefined) || !showEditOrganisation;
	const resourceGroupValid = resourceGroupValue != undefined || showEditOrganisation;

	useEffect(() => {
		setDisabled?.(!(organisationFieldValid && resourceGroupValid));
		// eslint-disable-next-line @eslint-react/hooks-extra/no-direct-set-state-in-use-effect
		setResourceGroupDisabled(!organisationFieldValid);
	}, [formState.isValid, setDisabled, selectedResourceGroup, showEditOrganisation, watch]);

	return (
		<Stack spacing={Dimens.spacingMd}>
			{showEditOrganisation && (
				<Controller
					name='organisationId'
					control={control}
					render={({ field }) => (
						<OrganisationsAutocomplete
							value={selectedOrganisationId}
							label={t('organisation.labels.single', { ns: 'entities' }) + '*'}
							onValueChanged={(newValue) => {
								setSelectedOrganisationId(newValue?.id);
								setSelectedResourceGroup(undefined);
								field.onChange(newValue?.id);
							}}
							required
							sx={{ width: '100%' }}
						/>
					)}
				/>
			)}
			<Controller
				name='resourceGroupId'
				control={control}
				render={({ field }) => (
					<ResourceGroupAutocomplete
						value={selectedResourceGroup}
						label={t('resourceGroup.labels.single', { ns: 'entities' })}
						disabled={resourceGroupDisabled}
						filterOrganisationId={selectedOrganisationId}
						onValueChanged={(newValue) => {
							setSelectedResourceGroup(newValue);
							field.onChange(newValue?.id);
						}}
						sx={{ width: '100%' }}
					/>
				)}
			/>
		</Stack>
	);
};

export default AssignKeyForm;
